import React, {useCallback, useEffect, useMemo, useState} from "react";
import {PREPARED_CART_ICON} from '../../../Constants';
import {
    Checkbox,
    DatePicker,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    Icon,
    IconButton,
    IPanelProps,
    IDropdownOption, Label, Link, mergeStyleSets,
    MessageBar,
    MessageBarButton,
    MessageBarType,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    TooltipHost,
    useTheme
} from "@fluentui/react";
import {
    useGetCompanyQuery,
    useGetCompanySuggestionsQuery,
    useGetContactsForCompanyQuery, 
    useGetWorkspaceProductsQuery,
    useGetWorkspaceQuery,
    useLazyGetCompaniesQuery,
    usePutWorkspaceMutation
} from "../../../store/Api";
import {
    useGetGlobalStorefrontSettingsQuery,
    useGetStorefrontQuery,
    useGetStorefrontsQuery
} from "../../../store/endpoints/StorefrontSettingsEndpoints";
import {
    useCalculatePriceEstimateMutation,
    useDeliverPreparedCartMutation,
    useGenerateDirectLinksMutation,
    useGetPreparedCartDeliveryEmailBodyQuery,
    useGetPreparedCartQuery,
    usePatchPreparedCartMutation
} from "../../../store/endpoints/PreparedCartEndpoints";
import {usePutWorkspaceItemDetailsMutation} from "../../../store/endpoints/WorkspaceItemsEndpoints";
import {IWorkspaceItem} from "../../../models/Workspace";
import RichTextField from "../../Common/RichTextField";
import ToggleField from "../../Common/ToggleField";
import AutocompleteSearchBox, {ISuggestionItem} from "../../Common/AutocompleteSearchBox";
import {ICompany} from "../../../models/Company";
import {IPreparedCart} from "../../../models/PreparedCart";
import {IPreparedCartDirectLink} from "../../../models/Storefront";
import {useGetOrganizationQuery} from "../../../store/endpoints/OrganizationsEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../../../store/rootReducer";
import {debounce} from "lodash";
import DataField from "../../Common/DataField";
import NumberField from "../../Inputs/NumberField";
import MarkupTypeSelection from "../MarkupTypeSelection";
import DropdownField from "../../Common/DropdownField";
import StorefrontDetailsPanel from "../StorefrontDetailsPanel";
import CompanyDetailsModal from "../../Contacts/CompanyDetailsModal";
import ContactDetailsModal from "../../Contacts/ContactDetailsModal";
import SuggestionPill from "../../Common/SuggestionPill";
import {openGlobalSearch} from "../../GlobalSearch/useGlobalSearchHotkey";
import {CompanySuggestionReason, ICompanySuggestion} from "../../../models/Company";

interface PreparedCartPanelProps {
    visible: boolean;
    onClose: () => void;
    workspaceId: string;
    /** Open on the final (E-Mail) step, next to the Deliver action — used when an import flow
     * hands off a fully-built workspace ready to send. */
    focusDeliver?: boolean;
    /** Workspace item ids the price estimate could not source; lets the page highlight those rows. */
    onProblemItemsChange?: (itemIds: string[]) => void;
}

/** Fixed panel width so the page can slide its content over by exactly this much (see WorkspacePage). */
export const PREPARED_CART_PANEL_WIDTH = 644;

/** The field a deliver blocker points at, and the tab that holds it. */
type BlockerField = 'storefront' | 'company' | 'contacts' | 'emailSubject' | 'emailContent' | 'pricing' | 'expiration' | 'estimate';
const TAB_FOR_BLOCKER: Record<BlockerField, string> = {
    storefront: 'audience', company: 'audience', contacts: 'audience',
    emailSubject: 'email', emailContent: 'email',
    pricing: 'pricing', expiration: 'configure',
    // The estimate renders above the tabs, so land where delivery happens.
    estimate: 'email'
};

/** Icon per suggestion reason (see ICompanySuggestion.reason). */
const SUGGESTION_ICONS: Record<CompanySuggestionReason, string> = {
    storefrontOrders: 'Shop',
    preparedCarts: PREPARED_CART_ICON,
    orders: 'Trending12',
    recent: 'History'
};

export const PreparedCartPanel: React.FC<PreparedCartPanelProps> = ({visible, workspaceId, onClose, focusDeliver, onProblemItemsChange}) => {
    const selectedOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const myUser = useSelector((state: RootState) => state.auth.userName);
    const [workingCart, setWorkingCart] = useState<Partial<IPreparedCart>>();
    const emailLookup = useGetPreparedCartDeliveryEmailBodyQuery({cartId: workspaceId ?? '', storefrontId: workingCart?.storefrontId ?? '' }, {skip: (!workspaceId || workspaceId === '' || !workingCart?.storefrontId || workingCart.storefrontId === '')});
    const [companySearchText, setCompanySearchText] = useState<string>("");
    const [selectedCompany, setSelectedCompany] = useState<ICompany | undefined>();
    const [companyOptions, setCompanyOptions] = useState<ICompany[]>([]);
    const [inProgress, setInProgress] = React.useState(false);
    const [emailContent, setEmailContent] = useState<string | undefined>(emailLookup.data);
    const [dynamicSuggestions, setDynamicSuggestions] = React.useState<ISuggestionItem[]>();
    const workspace = useGetWorkspaceQuery(workspaceId, {skip: !workspaceId || workspaceId.trim().length === 0});

    const [repOptions, setRepOptions] = useState<IDropdownOption[]>([]);
    const [userOptions, setUserOptions] = useState<IDropdownOption[]>([]);
    const [canDeliver, setCanDeliver] = useState<boolean>(false);
    const [canGetLinks, setCanGetLinks] = useState<boolean>(false);
    const org = useGetOrganizationQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''});
    const [emailSubject, setEmailSubject] = useState<string>("Your Prepared Shopping Cart is Ready");
    const queryThreshold = 2;
    
    const storefronts = useGetStorefrontsQuery()
    const storefront = useGetStorefrontQuery(workingCart?.storefrontId ?? '', {skip: !workingCart?.storefrontId || workingCart.storefrontId === ''});
    const globalStorefrontSettings = useGetGlobalStorefrontSettingsQuery();
    const preparedCart = useGetPreparedCartQuery({workspaceId: workspaceId }, {skip: !workspaceId || workspaceId === ''});
    
    const [deliver] = useDeliverPreparedCartMutation()
    const [companySearch] = useLazyGetCompaniesQuery();
    const [savePreparedCart] = usePatchPreparedCartMutation();
    const [updateWorkspace] = usePutWorkspaceMutation();
    // debouncedSave is memoized, so it reads the latest workspace/company through refs.
    const workspaceRef = React.useRef(workspace.data);
    workspaceRef.current = workspace.data;
    const selectedCompanyRef = React.useRef(selectedCompany);
    selectedCompanyRef.current = selectedCompany;
    const getUsers = useGetContactsForCompanyQuery({companyId: selectedCompany?.id ?? ''}, {skip: !selectedCompany?.id || selectedCompany.id === ''});
    const [isDelivering, setIsDelivering] = useState(false);
    const [isValidEstimate, setIsValidEstimate] = useState(false);
    const [, setEstimating] = useState(false);
    const [showStorefrontEditor, setShowStorefrontEditor] = useState(false);
    const [showCompanyEditor, setShowCompanyEditor] = useState(false);
    const [showContactEditor, setShowContactEditor] = useState(false);
    const [defaultMarkup, setDefaultMarkup] = useState<number>(0);
    const [priceError, setPriceError] = useState<boolean>();
    const [costError, setCostError] = useState<boolean>();
    const [minimumPriceError, setMinimumPriceError] = useState<boolean>();
    
    const company = useGetCompanyQuery(workingCart?.targetCompany ?? '', {skip: !workingCart?.targetCompany || workingCart.targetCompany === ''});

    const [saveStatus, setSaveStatus] = useState<string>("");
    const [saveStatusType, setSaveStatusType] = useState<MessageBarType>(MessageBarType.info);
    const [showDeliveryConfirmation, setShowDeliveryConfirmation] = useState(false);
    const [deliverError, setDeliverError] = useState<string>();
    const [blockerField, setBlockerField] = useState<BlockerField>();
    // Controlled tab: the quick flow lands on whichever tab holds the first blocker, then guides
    // the user field by field until the cart can deliver.
    const [selectedTab, setSelectedTab] = useState<string>(focusDeliver ? 'email' : 'audience');
    const [guideActive, setGuideActive] = useState(false);
    const landed = React.useRef(false);
    // ThemeProvider theme (getTheme() would hand back Fluent's default blue palette).
    const theme = useTheme();
    // Everything the form and its readiness checks depend on. Rendering piecemeal as each of these
    // arrived made the panel jump around, so the body waits for all of them the first time through;
    // after that, refetches update in place rather than collapsing back to a spinner.
    const cartSettled = preparedCart.isError || (preparedCart.isSuccess && !!workingCart);
    const companySettled = !workingCart?.targetCompany || !!selectedCompany || company.isError;
    const contactsSettled = !selectedCompany?.id || getUsers.data !== undefined || getUsers.isError;
    const emailSettled = !workingCart?.storefrontId || emailLookup.isError
        || (!emailLookup.isFetching && (emailLookup.data === undefined || !!emailContent));
    const dataSettled = cartSettled && !storefronts.isLoading && companySettled && contactsSettled && emailSettled;
    const hasSettledOnce = React.useRef(false);
    if (dataSettled) hasSettledOnce.current = true;
    const panelReady = hasSettledOnce.current || dataSettled;

    const guideStyle = (field: BlockerField): React.CSSProperties | undefined => guideActive && blockerField === field
        // The 4px offset reaches above the wrapper, so leave a 6px gap for it (Fluent fields sit flush
        // on each other) and paint above the positioned dropdown before it.
        ? {outline: `2px solid ${theme.palette.themePrimary}`, outlineOffset: 4, borderRadius: 2, marginTop: 6, position: 'relative', zIndex: 1}
        : undefined;
    const [confirmUnpublish, setConfirmUnpublish] = useState(false);
    const [showDirectLinksModal, setShowDirectLinksModal] = useState(false);
    const [directLinks, setDirectLinks] = useState<IPreparedCartDirectLink[]>([]);
    const [directLinksError, setDirectLinksError] = useState<string | undefined>();
    const [savingChanges, setSavingChanges] = useState<boolean>();
    
    const [fetchingLinks, setFetchingLinks] = useState<boolean>(false);
    const isInitialized = React.useRef(false);
    
    const [links] = useGenerateDirectLinksMutation();


    useEffect(() => {
        if(storefront?.data?.defaultMarkup != null) {
            setDefaultMarkup(storefront.data.defaultMarkup);
        }
        else if(globalStorefrontSettings?.data?.defaultMarkup != null) {
            setDefaultMarkup(globalStorefrontSettings.data.defaultMarkup);
        }
    }, [storefront.data, globalStorefrontSettings.data]);

    useEffect(() => { 
        return () => {
            setConfirmUnpublish(false);
            setIsDelivering(false);
            setWorkingCart(undefined);
            setSelectedCompany(undefined);
            setCompanySearchText("");
            setEmailContent(undefined);
            setRepOptions([]);
            setUserOptions([]);
            setCanDeliver(false);
            setFetchingLinks(false)
            setCanGetLinks(false);
            setEmailSubject("Your Prepared Shopping Cart is Ready");
        }
    }, []);
    
    // Hide the save message after a few seconds if successful
    useEffect(() => {
        if (saveStatusType === MessageBarType.success && saveStatus) {
            const timeout = setTimeout(() => {
                setSaveStatus("");
            }, 2500);
            return () => clearTimeout(timeout);
        }
    }, [saveStatus, saveStatusType]);

    // Hide delivery confirmation after a few seconds
    useEffect(() => {
        if (showDeliveryConfirmation) {
            const timeout = setTimeout(() => {setShowDeliveryConfirmation(false); onClose()}, 10000);
            return () => clearTimeout(timeout);
        }
    }, [showDeliveryConfirmation, onClose]);

    const debouncedSave = useCallback(
        debounce(async (cart: Partial<IPreparedCart>) => {
            setSaveStatus("Saving...");
            setSavingChanges(true)
            setSaveStatusType(MessageBarType.info);
            try {
                await savePreparedCart({
                    id: preparedCart.data?.id ?? '',
                    request: {
                        ...cart
                    }
                });
                // The workspace header pill shows one customer for the workspace; keep it the cart's.
                const ws = workspaceRef.current;
                const cartContact = cart.targetUsers?.[0] ?? '';
                if (ws?.id && ((cart.targetCompany ?? '') !== (ws.companyId ?? '') || cartContact !== (ws.contactId ?? ''))) {
                    await updateWorkspace({
                        id: ws.id,
                        companyId: cart.targetCompany ?? '',
                        companyName: cart.targetCompany ? (selectedCompanyRef.current?.companyName ?? ws.companyName ?? '') : '',
                        contactId: cartContact,
                        documentIsCurrent: false
                    }).unwrap().catch(() => undefined);
                }
                setSavingChanges(false)
                setSaveStatus("Saved");
                setSaveStatusType(MessageBarType.success);
            } catch (e) {
                setSavingChanges(false)
                setSaveStatus("Error saving changes");
                setSaveStatusType(MessageBarType.error);
            }
        }, 800),
        [preparedCart.data, savePreparedCart, updateWorkspace]
    );

    // Save on workingCart change
    useEffect(() => {
        if (!preparedCart.data || !isInitialized.current) return;
        debouncedSave(workingCart ?? {});
        return () => { debouncedSave.cancel(); };
    }, [workingCart, preparedCart.data, debouncedSave]);

    // Reset company state when workspaceId changes to avoid stale selectedCompany
    useEffect(() => {
        isInitialized.current = false;
        setSelectedCompany(undefined);
        setCompanySearchText('');
        setWorkingCart(undefined);
        landed.current = false;
        hasSettledOnce.current = false;
        setGuideActive(false);
        setSelectedTab(focusDeliver ? 'email' : 'audience');
    }, [workspaceId]);

    useEffect(() => {
        if(preparedCart.data){
            isInitialized.current = false;
            const initialCart: Partial<IPreparedCart> = {
                ...preparedCart.data,
                ...(!preparedCart.data.salesRep ? {salesRep: myUser} : {}),
                ...(!preparedCart.data.name && workspace.data?.name ? {name: workspace.data.name + " - Prepared Cart"} : {}),
            };
            setWorkingCart(initialCart);
            // Flip after the render triggered by setWorkingCart has completed
            setTimeout(() => { isInitialized.current = true; }, 0);
        }
    }, [preparedCart.data, workspace.data?.name]);

    useEffect(() => {
        if(company.data && workingCart?.targetCompany && company.data.id === workingCart.targetCompany){
            setSelectedCompany(company.data);
            setCompanySearchText(company.data?.companyName ?? '');
        }
    }, [company.data, workingCart?.targetCompany]);
    
    useEffect(() => {
        if(emailLookup.data) {
            setEmailContent(emailLookup.data);
        }
    }, [emailLookup.data])

    useEffect(() => {
        if(org.data){
            const options = org.data.users?.map((u) => ({
                key: u.username ?? '',
                text: u.username ?? ''
            }))
            setRepOptions(options ?? []);
        }
    }, [org.data]);
    
    useEffect(() => {
        if(getUsers.data){
            const options = getUsers.data.items.map((s) => ({
                key: s.id ?? '',
                text: `${s.firstName ? (s.firstName + ' ') : ''}${s.lastName ? (s.lastName + ' ') : ''} - ${s.emailAddress ?? ''}`
            }))
            setUserOptions(options);
        }
    }, [getUsers.data, company.data])

    useEffect(() => {
        setCanDeliver(false)
        setBlockerField(undefined);
        if(workingCart?.ordered){
            setDeliverError("Cart has been ordered and cannot be changed");
        }
        else if(!workingCart?.storefrontId) {
            setDeliverError("Select a storefront");
            setBlockerField('storefront');
        }
        else if(!selectedCompany) {
            setDeliverError("Select a customer company");
            setBlockerField('company');
        }
        else if(!workingCart?.targetUsers || workingCart.targetUsers.length === 0) {
            setDeliverError("Select at least one target customer contact");
            setBlockerField('contacts');
        }
        else if(!emailSubject) {
            setDeliverError("Please enter an email subject");
            setBlockerField('emailSubject');
        }
        else if(!emailContent) {
            setDeliverError("Please prepare the email content");
            setBlockerField('emailContent');
        }
        else if(priceError){
            setDeliverError("Calculated price is zero or negative. Please review the cart details and try again.");
            setBlockerField('pricing');
        }
        else if(costError){
            setDeliverError("Calculated cost is negative. Please review the cart details and try again.");
            setBlockerField('pricing');
        }
        else if(minimumPriceError) {
            setDeliverError("Calculated price is below the minimum price. Please review the cart details and try again.");
            setBlockerField('pricing');
        }
        else if(!isValidEstimate) {
            setDeliverError("Workspace contains unpurchasable items. Replace or remove these items to proceed.");
            setBlockerField('estimate');
        }
        else if(workingCart?.delivered){
            setDeliverError("This cart has already been delivered.");
        }
        else if(workingCart?.expirationDate && new Date(workingCart.expirationDate) < new Date()){
            setDeliverError("This cart has expired. Update the expiration date to proceed.");
            setBlockerField('expiration');
        }
        else {
            setDeliverError(undefined);
            setCanDeliver(true)
        }
    }, [selectedCompany, workingCart, emailSubject, emailContent, isValidEstimate, priceError, costError, minimumPriceError]);

    // Quick-flow landing: wait until the cart and everything the readiness checks depend on has
    // loaded, then open the tab holding the first blocker (or the deliver tab when nothing blocks).
    useEffect(() => {
        if (!focusDeliver || landed.current || !visible || !dataSettled) return;
        landed.current = true;
        setSelectedTab(blockerField ? TAB_FOR_BLOCKER[blockerField] : 'email');
        setGuideActive(!!blockerField);
    }, [focusDeliver, visible, dataSettled, blockerField]);

    useEffect(() => {
        setCanGetLinks(false)
        if(isValidEstimate && workingCart?.storefrontId && !minimumPriceError && !costError && !priceError && (workingCart?.targetUsers && workingCart.targetUsers.length > 0) && selectedCompany && !workingCart.ordered){
            setCanGetLinks(true)
        }
    }, [selectedCompany, workingCart, emailSubject, emailContent, isValidEstimate, priceError, costError, minimumPriceError]);

    useEffect(() => {
        if(storefront.data?.preparedCartEmailSubject && storefront.data.preparedCartEmailSubject !== '') {
            setEmailSubject(storefront.data.preparedCartEmailSubject);
        }
        
    }, [storefront.data]);
    
    
    const storefrontOptions: IDropdownOption[] =  useMemo(() => {
        if (!storefronts.data) return [];
        return storefronts.data.items.map((s) => ({
            key: s.id ?? '',
            text: s.name ?? '',
            data: {favicon: s.favicon || undefined}
        }));
    }, [storefronts.data]);

    /** Storefront option: its favicon (when it has one) beside the name, in the list and in the selected title. */
    const renderStorefrontOption = (option?: IDropdownOption) => {
        if (!option) return null;
        const favicon = (option.data as {favicon?: string} | undefined)?.favicon;
        return (
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.5em', minWidth: 0}}>
                {favicon
                    ? <img src={favicon} alt='' width={16} height={16} style={{flexShrink: 0, objectFit: 'contain'}} />
                    : <Icon iconName='Shop' styles={{root: {fontSize: 14, width: 16, textAlign: 'center', color: theme.palette.neutralSecondary}}} />}
                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{option.text}</span>
            </span>
        );
    };
    
    const stripHtml = (html: string): string => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent ?? tmp.innerText ?? '';
    };

    const openMailto = (emailAddress: string, link: string) => {
        const body = `${stripHtml(emailContent ?? "")}\n\n${link}`;
        window.location.href = `mailto:${encodeURIComponent(emailAddress)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(body)}`;
    };

    const getLinks = async () => {
        setDirectLinksError(undefined);
        setDirectLinks([]);
        setFetchingLinks(true)
        try {
            const result = await links({workspaceId: workspaceId});
            if ('error' in result) {
                setDirectLinksError("Failed to generate direct links. Please try again.");
            } else if (result.data?.links && result.data.links.length > 0) {
                setDirectLinks(result.data.links);
            } else {
                setDirectLinksError("No links were generated. Ensure the cart has been delivered to at least one contact.");
            }
        } catch (e) {
            setDirectLinksError("An unexpected error occurred while generating links.");
        }
        setShowDirectLinksModal(true);
        setFetchingLinks(false);
    };

    const getDeliverButton =() => {
        if(workingCart?.ordered){
            return <></>
        }
        if(workingCart?.delivered || workingCart?.delivered){
            return <DefaultButton
                text="Unlock" 
                style={{width: '50%' }} 
                onClick={() => setWorkingCart((current) => {return {...current, ordered: false, delivered: false}})}
                iconProps={{iconName: 'Unlock'}}
            />
        }
        return(<PrimaryButton
            text="Deliver"
            iconProps={{iconName: 'Send' }}
            style={{width: '50%' }}
            onClick={async () => {
                setIsDelivering(true);
                try {
                    await savePreparedCart({
                        id: preparedCart.data?.id ?? '', request: { ...workingCart, delivered: true, active: true
                        }
                    });
                    await deliver({id: preparedCart.data?.id ?? '',
                        request: {
                            id: preparedCart.data?.id ?? '',
                            emailSubject: emailSubject,
                            emailBody: emailContent
                        }
                    });
                    setShowDeliveryConfirmation(true);
                } finally {
                    setIsDelivering(false);
                }
            }}
            disabled={isDelivering || !canDeliver}
        />)
    }
    
    
    const footer = (
        <Stack tokens={{ childrenGap: 4 }} styles={{ root: { width: '100%' } }}>
            {saveStatus && (
                <MessageBar messageBarType={saveStatusType} isMultiline={false}>
                    {saveStatus}
                </MessageBar>
            )}
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                {getDeliverButton()}
                {fetchingLinks ?
                    <Spinner size={SpinnerSize.xSmall} label={'Fetching Links'}/> :
                    <DefaultButton text={"Get Links"} onClick={getLinks} iconProps={{iconName: 'link'}} style={{ width: '25%', color: '#224afa' }} disabled={isDelivering || !canGetLinks || fetchingLinks} />
                }
                <DefaultButton text="Cancel" onClick={onClose} style={{ width: '25%' }} iconProps={{iconName: 'cancel'}} disabled={isDelivering} />
            </Stack>
        </Stack>
    );

    const onSuggestionClicked = (suggestion: string | ISuggestionItem) => {
        if(typeof  suggestion === "string") {
            return;
        }
        const matchedCompany = companyOptions.find(c => c.id === suggestion.getKey());
        if(!matchedCompany) {
            return;
        }
        setSelectedCompany(matchedCompany);
        setWorkingCart((current) => {return {...current, targetUsers: [], targetCompany: suggestion.getKey()}});


    };

    const load = async (value: string) => {
        if (value && value != '') {
            companySearch({query: {query: value}})
                .unwrap().then((result) => {
                    if (result?.items) {
                        const options = result.items.map(
                            (val: ICompany) => new CompanySearchOption(val.id ?? '', val.companyName ?? '', val.id ?? '')
                        )
                        setCompanyOptions(() => [...result.items]);
                        setDynamicSuggestions(options)
                    }
                    setInProgress(false)
                }
            )
        }
    }
    const onChangeDynamic = async (newText?: string) => {
        if (!newText || newText.trim().length < queryThreshold) {
            setDynamicSuggestions(undefined);
        } else {
            setInProgress(true);
            await load(newText)
        }
    };

    const canAddContact = !!selectedCompany?.id && !isDelivering && !workingCart?.ordered && !workingCart?.delivered;
    // With nobody to pick, the contacts dropdown is noise; the "add one" bar below it is the whole story.
    const companyHasNoContacts = canAddContact && getUsers.data !== undefined && getUsers.data.items.length === 0;

    // Suggested companies for the blind company search; only while nothing is picked and the cart is still editable.
    const showCompanySuggestions = !selectedCompany && !workingCart?.targetCompany && !isDelivering && !workingCart?.ordered && !workingCart?.delivered;
    const companySuggestions = useGetCompanySuggestionsQuery(
        {storefrontId: workingCart?.storefrontId || undefined, limit: 6},
        {skip: !showCompanySuggestions});
    // Clearing must reach the server too: the PATCH only touches targetCompany when it is non-null,
    // so an empty string (not undefined) is what actually unsets it.
    const clearSelectedCompany = () => {
        setSelectedCompany(undefined);
        setCompanySearchText('');
        setDynamicSuggestions(undefined);
        setWorkingCart((current) => ({...current, targetUsers: [], targetCompany: ''}));
    };
    const pickSuggestedCompany = (suggestion: ICompanySuggestion) => {
        setSelectedCompany({id: suggestion.companyId, companyName: suggestion.companyName});
        setCompanySearchText(suggestion.companyName ?? '');
        setWorkingCart((current) => ({...current, targetUsers: [], targetCompany: suggestion.companyId}));
    };

    const getPlaceholder = () => {
        if(selectedCompany){
            return `Selected Value: ${selectedCompany.companyName}`
        }
        return `Search Storefront Companies`
    }

    const contentStyles = mergeStyleSets({
        root: {
            zIndex: '10 !important',
            marginTop: 'var(--app-header-offset)',
        },
        main: {
            marginTop: 'var(--app-header-offset)',
        },
        scrollableContent: {
            backgroundColor: 'rgb(255, 255, 255)',
        },
        navigation: {
            backgroundColor: 'rgb(255, 255, 255)',
        },
        footerInner: {
            backgroundColor: 'rgb(255, 255, 255)',
        },
        content: {
            backgroundColor: 'rgb(255, 255, 255)',
        },
        commands: {
            backgroundColor: 'rgb(255, 255, 255)',
        },
    });
    
    return (
        <Panel
            isOpen={visible}
            onDismiss={onClose}
            type={PanelType.custom}
            customWidth={`${PREPARED_CART_PANEL_WIDTH}px`}
            // Sits beside the workspace grid like the search criteria panel: no overlay, the grid
            // stays interactive, and the page shifts its content over by the panel width.
            isBlocking={false}
            headerText="Customize Prepared Shopping Cart"
            onRenderHeader={(headerProps?: IPanelProps, _defaultRender?: unknown, headerTextId?: string) =>
                <div style={{flexGrow: 1, display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0 24px', minWidth: 0}}>
                    <Icon iconName={PREPARED_CART_ICON} aria-hidden style={{fontSize: 32, color: theme.palette.themePrimary, flexShrink: 0}} />
                    <span id={headerTextId} role='heading' aria-level={1}
                        style={{fontSize: 20, fontWeight: 600, lineHeight: '28px', color: theme.palette.neutralPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {headerProps?.headerText}
                    </span>
                </div>}
            isFooterAtBottom
            styles={{
                root: {
                    zIndex: 10,
                    marginTop: 'var(--app-header-offset)'
                },
                content:contentStyles.content,
                commands:contentStyles.commands,
                scrollableContent: contentStyles.scrollableContent,
                navigation: contentStyles.navigation,
                footerInner: contentStyles.footerInner,
            }}
            onRenderFooterContent={() => footer}
            closeButtonAriaLabel="Close"
        >
            {showDeliveryConfirmation && (
                <Dialog
                    hidden={!showDeliveryConfirmation}
                    dialogContentProps={{
                        type: DialogType.largeHeader,
                        title: 'Prepared Cart Delivered',
                        subText: 'The prepared cart has been delivered.',
                    }}
                    modalProps={{
                        isBlocking: true,
                    }}
                >
                    <DialogFooter>
                        <DefaultButton
                            primary
                            text='OK'
                            onClick={() => {
                                setShowDeliveryConfirmation(false)
                                onClose();
                            }}
                        />
                    </DialogFooter>
                </Dialog>
            )}

            {showDirectLinksModal && (
                <Dialog
                    hidden={!showDirectLinksModal}
                    dialogContentProps={{
                        type: DialogType.largeHeader,
                        title: 'Prepared Cart Direct Links',
                        subText: 'Links valid for 24 hours'
                    }}
                    modalProps={{
                        isBlocking: true,
                    }}
                    minWidth={480}
                >
                    {directLinksError ? (
                        <MessageBar messageBarType={MessageBarType.error} isMultiline>
                            {directLinksError}
                        </MessageBar>
                    ) : (
                        <Stack tokens={{ childrenGap: 10 }}>
                            {directLinks.map((link) => (
                                <Stack key={link.emailAddress} horizontal tokens={{ childrenGap: 6 }} verticalAlign="end">
                                    <TextField
                                        label={link.emailAddress}
                                        value={link.link}
                                        readOnly
                                        styles={{ root: { flexGrow: 1 } }}
                                    />
                                    <TooltipHost content="Copy to clipboard">
                                        <IconButton
                                            iconProps={{ iconName: 'Copy' }}
                                            ariaLabel="Copy link"
                                            onClick={() => navigator.clipboard.writeText(link.link)}
                                        />
                                    </TooltipHost>
                                    <TooltipHost content="Open in mail client">
                                        <IconButton
                                            iconProps={{ iconName: 'Mail' }}
                                            ariaLabel="Open in mail client"
                                            onClick={() => openMailto(link.emailAddress, link.link)}
                                        />
                                    </TooltipHost>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                    <DialogFooter>
                        <DefaultButton
                            primary
                            text='Close'
                            onClick={() => setShowDirectLinksModal(false)}
                        />
                    </DialogFooter>
                </Dialog>
            )}

            {confirmUnpublish && (
                <Dialog
                    hidden={!confirmUnpublish}
                    dialogContentProps={{
                        type: DialogType.largeHeader,
                        title: 'Confirm Unpublish',
                        subText: 'This will unpublish the cart and prevent delivered customers from accessing it. Are you sure you want to unpublish?',
                    }}
                    modalProps={{
                        isBlocking: true,
                    }}
                >
                    <DialogFooter>
                        <PrimaryButton
                            primary
                            text='Yes, Unpublish'
                            onClick={() => {
                                setWorkingCart((current) => {return {...current, active: false}} );
                                setConfirmUnpublish(false)
                            }}
                        />
                        <DefaultButton
                            primary
                            text='No'
                            onClick={() => {
                                setConfirmUnpublish(false)
                            }}
                        />
                    </DialogFooter>
                </Dialog>
            )}
            {workingCart?.storefrontId && showStorefrontEditor && 
            <StorefrontDetailsPanel storefrontId={workingCart?.storefrontId} isOpen={showStorefrontEditor} onClose={() => setShowStorefrontEditor(false)}/>
            }
            {workingCart?.targetCompany && showCompanyEditor && 
            <CompanyDetailsModal company={workingCart?.targetCompany} isOpen={showCompanyEditor} onClose={() => setShowCompanyEditor(false)}/>
            }
            {selectedCompany?.id && showContactEditor &&
            <ContactDetailsModal
                isOpen={showContactEditor}
                defaultCompany={selectedCompany.id}
                onDismiss={(saved, contactId) => {
                    setShowContactEditor(false);
                    if (!saved) return;
                    // The new contact is what the rep came here for: refresh the list and select it.
                    if (!getUsers.isUninitialized) getUsers.refetch();
                    if (contactId) {
                        setWorkingCart((current) => ({
                            ...current,
                            targetUsers: current?.targetUsers?.includes(contactId) ? current.targetUsers : [...current?.targetUsers ?? [], contactId]
                        }));
                    }
                }}
            />
            }
            
            
            {isDelivering ? (
                <Stack verticalAlign="center" horizontalAlign="center" style={{height: '100%', minHeight: 400}}>
                    <Spinner size={SpinnerSize.large} label="Delivering..." />
                </Stack>
            ) : (
            !panelReady ? <Spinner label='Loading prepared cart…' styles={{root: {marginTop: '2em'}}} /> : 
                <React.Fragment>
                    {workingCart?.delivered && workingCart?.ordered != true && 
                    <ToggleField
                        label="Published"
                        checked={workingCart?.active}
                        onChange={(_, checked) => {
                            if(workingCart?.delivered && !checked){
                                setConfirmUnpublish(true);
                            }
                            else {
                                setWorkingCart((current) => {
                                    return {...current, active: checked}
                                })
                            }
                        }}
                        disabled={isDelivering || workingCart?.ordered}
                    />
                    }
                    <div style={{marginTop: '1em'}} />
                    <PreparedCartPriceEstimate
                        waitForSave={savingChanges}
                        onEstimateBegin={() => setEstimating(true)}
                        onProblemItemsChange={onProblemItemsChange}
                        readiness={{canDeliver, blocker: deliverError, blockerField}}
                        onEstimate={(result, priceError, costError, minimumPriceError) =>{
                            setIsValidEstimate(result);
                            setPriceError(priceError);
                            setCostError(costError);
                            setMinimumPriceError(minimumPriceError);
                            setEstimating(false);
                        }}
                        workspaceId={workspaceId}
                        storefrontId={workingCart?.storefrontId}
                        markupType={workingCart?.markupType}
                        markup={workingCart?.markup ?? defaultMarkup}
                        minimumPrice={workingCart?.minimumPrice}
                    />
                    <Pivot selectedKey={selectedTab} onLinkClick={(item) => item?.props.itemKey && setSelectedTab(item.props.itemKey)}>
                        <PivotItem headerText="1). Audience" itemKey='audience' >
                            <div style={guideStyle('storefront')}>
                            <DropdownField
                                key={storefronts.isLoading ? 'storefronts-loading' : 'storefronts-ready'}
                                required={true}
                                label="Storefront"
                                options={storefrontOptions}
                                placeholder={storefronts.isLoading ? 'Loading storefronts...' : 'Select a storefront'}
                                labelActions={workingCart?.storefrontId ? <IconButton iconProps={{iconName: 'EntryView'}} onClick={()=> setShowStorefrontEditor(true)}/>: undefined}
                                selectedKey={workingCart?.storefrontId}
                                onRenderOption={(option) => renderStorefrontOption(option)}
                                onRenderTitle={(options) => renderStorefrontOption(options?.[0])}
                                onChange={(_, option) => setWorkingCart((current) => {return {...current, storefrontId: option?.key as string ?? ''}})}
                                disabled={isDelivering || workingCart?.ordered || workingCart?.delivered || storefronts.isLoading}
                            />
                            </div>
                            <div style={guideStyle('company')}>
                            <AutocompleteSearchBox
                                label="Customer Company"
                                labelActions={workingCart?.targetCompany ? <IconButton iconProps={{iconName: 'EntryView'}} onClick={()=> setShowCompanyEditor(true)}/>: undefined}
                                required={true}
                                placeholder={getPlaceholder()}
                                onChange={async (_, newValue) => await onChangeDynamic(newValue)}
                                onSuggestionClicked={onSuggestionClicked}
                                value={companySearchText}
                                suggestions={dynamicSuggestions}
                                inProgress={inProgress}
                                debounceTime={500}
                                onClear={clearSelectedCompany}
                                selectedValue={selectedCompany ? {
                                    key: selectedCompany.id ?? '',
                                    text: selectedCompany.companyName || companySearchText || 'Selected company',
                                    iconName: 'CityNext2'
                                } : undefined}
                                onClearSelection={clearSelectedCompany}
                                disabled={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            </div>
                            {showCompanySuggestions && (companySuggestions.data?.length ?? 0) > 0 && companySuggestions.data &&
                                <Stack horizontal wrap verticalAlign='center' tokens={{childrenGap: '0.5em'}} styles={{root: {marginTop: '0.5em'}}}>
                                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>Suggested</Text>
                                    {companySuggestions.data.map((s: ICompanySuggestion) =>
                                        <SuggestionPill
                                            key={s.companyId}
                                            text={s.companyName ?? s.companyId}
                                            iconName={SUGGESTION_ICONS[s.reason] ?? 'CityNext2'}
                                            tooltip={<div>{s.companyName}{s.detail && <div>{s.detail}</div>}</div>}
                                            ariaLabel={`Select ${s.companyName ?? 'company'}. ${s.detail ?? ''}`}
                                            onClick={() => pickSuggestedCompany(s)}
                                        />)}
                                </Stack>}
                            <div style={guideStyle('contacts')}>
                            {!companyHasNoContacts && <Dropdown
                                disabled={!selectedCompany || isDelivering || workingCart?.ordered || workingCart?.delivered}
                                required={true}
                                label="Customer Contacts"
                                onRenderLabel={(dropdownProps) =>
                                    <div style={{display: 'inline-flex'}}>
                                        <Label required={dropdownProps?.required} disabled={dropdownProps?.disabled}>{dropdownProps?.label}</Label>
                                        {canAddContact &&
                                            <TooltipHost content={`Add a contact to ${selectedCompany?.companyName ?? 'this company'}`}>
                                                <IconButton iconProps={{iconName: 'AddFriend'}} ariaLabel='Add a contact' onClick={() => setShowContactEditor(true)}/>
                                            </TooltipHost>}
                                    </div>}
                                options={userOptions}
                                selectedKeys={workingCart?.targetUsers}
                                multiSelect
                                onChange={(_, option) => {
                                    if (!option) return;
                                    const users = option.selected
                                        ? [...workingCart?.targetUsers ?? [], option.key as string]
                                        : workingCart?.targetUsers?.filter((key) => key !== option.key)
                                    setWorkingCart({...workingCart, targetUsers: users});
                                }}
                            />}
                            {companyHasNoContacts &&
                                <MessageBar messageBarType={MessageBarType.info} styles={{root: {marginTop: '0.5em'}}}
                                    actions={<div><MessageBarButton onClick={() => setShowContactEditor(true)}>Add Contact</MessageBarButton></div>}>
                                    {selectedCompany?.companyName ?? 'This company'} has no contacts. Add one to deliver the cart.
                                </MessageBar>}
                            </div>
                        </PivotItem>
                        <PivotItem headerText="2). Pricing" itemKey='pricing'>
                            <div style={guideStyle('pricing')}>
                            <NumberField
                                disabled={isDelivering || workingCart?.ordered}
                                label='Minimum Price'
                                value={workingCart?.minimumPrice}
                                onChange={(v) => setWorkingCart({...workingCart, minimumPrice: v})}
                                prefix='$'
                                decimal={true}
                            />
                            </div>
                            <NumberField
                                disabled={isDelivering || workingCart?.ordered}
                                label='Default Markup'
                                value={workingCart?.markup}
                                onChange={(v) => setWorkingCart({...workingCart, markup: v})}
                                decimal={true}
                                placeholder={`${defaultMarkup} (from Storefront)`}
                            />
                            <MarkupTypeSelection 
                                disabled={isDelivering || workingCart?.ordered}
                                label='Default Markup Type'
                                value={workingCart?.markupType}
                                onChange={(v) => setWorkingCart({...workingCart, markupType: v})}
                            />
                            <ToggleField
                                disabled={isDelivering || workingCart?.ordered}
                                label="Show Original Price"
                                checked={workingCart?.showOriginalPrice}
                                onChange={(_, checked) => setWorkingCart({...workingCart, showOriginalPrice: checked})}
                            />
                        </PivotItem>
                        <PivotItem headerText="3). Customize" itemKey='customize'>
                            <DataField
                                label="Name"
                                value={workingCart?.name}
                                onChange={(e, v) => setWorkingCart({...workingCart, name: v ?? ''})}
                                disabled={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            <RichTextField
                                label="Description"
                                value={workingCart?.description}
                                onChange={(v) => setWorkingCart({...workingCart, description: v})}
                                readOnly={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            <RichTextField
                                label="Instructions"
                                value={workingCart?.instructions}
                                onChange={(v) => setWorkingCart({...workingCart, instructions: v})}
                                multiline
                                readOnly={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                        </PivotItem>
                        <PivotItem headerText="4). Configure" itemKey='configure'>
                            <ToggleField
                                label="Can Add Additional Items"
                                toolTip="Lets the customer add more products or bundles from the storefront to this cart. Product cards and the cart page show an add-to-cart action, and the cart summary gets an 'Add more products' button. Items they add can be removed or re-quantified by them."
                                checked={workingCart?.canAddAdditionalItems}
                                onChange={(_, checked) => setWorkingCart({...workingCart, canAddAdditionalItems: checked})}
                                disabled={isDelivering || workingCart?.ordered}
                            />
                            <ToggleField
                                label="Can Change Item Quantities"
                                toolTip="Lets the customer change the quantity of the lines you prepared. Off, the quantities you set are fixed at checkout (lines the customer added themselves stay editable)."
                                checked={workingCart?.canModifyItemQuantities}
                                onChange={(_, checked) => setWorkingCart({...workingCart, canModifyItemQuantities: checked})}
                                disabled={isDelivering || workingCart?.ordered}
                            />
                            <ToggleField
                                label="Can Share Cart"
                                toolTip="Shows a Share button in the cart summary so the customer can pass the cart to a colleague to review or buy. Sharing is blocked automatically while the cart holds a quote-required item."
                                checked={workingCart?.canShare}
                                onChange={(_, checked) => setWorkingCart({...workingCart, canShare: checked})}
                                disabled={isDelivering || workingCart?.ordered}
                            />
                            <ToggleField
                                label="Allow Customer Contact"
                                toolTip="Adds a comment button to the cart so the customer can send you a question or note about this cart from the storefront."
                                checked={workingCart?.allowCustomerContact}
                                onChange={(_, checked) => setWorkingCart({...workingCart, allowCustomerContact: checked})}
                                disabled={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            <ToggleField
                                label="Allow Bundle Configuration"
                                toolTip="Lets the customer open the bundles you prepared and change their options (selected products, quantities). Off, prepared bundles are fixed; bundles the customer adds follow 'Can Add Additional Items'."
                                checked={workingCart?.allowBundleConfiguration}
                                onChange={(_, checked) => setWorkingCart({...workingCart, allowBundleConfiguration: checked})}
                                disabled={isDelivering || workingCart?.ordered}
                            />
                            <div style={guideStyle('expiration')}>
                            <TooltipHost content="After this date the cart can no longer be delivered or checked out; the customer sees it as expired. Leave it empty for a cart that never expires.">
                            <DatePicker
                                label="Expiration Date"
                                value={(workingCart?.expirationDate ? new Date(workingCart.expirationDate) : undefined)}
                                onSelectDate={(date) => setWorkingCart({...workingCart, expirationDate: date?.toISOString() ?? undefined})}
                                placeholder="Select a date"
                                disabled={isDelivering || workingCart?.ordered}
                            />
                            </TooltipHost>
                            </div>
                        </PivotItem>
                        <PivotItem headerText="5). E-Mail" itemKey='email'>
                            <Dropdown
                                required={true}
                                label="Sales Rep"
                                options={repOptions}
                                selectedKey={workingCart?.salesRep}
                                onChange={(_, option) => setWorkingCart({...workingCart, salesRep: option?.key as string ?? ""})}
                                disabled={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            <div style={guideStyle('emailSubject')}>
                            <TextField
                                label="Email Subject"
                                value={emailSubject}
                                onChange={(e, v) => {
                                    setEmailSubject(v ?? '');
                                }}
                                disabled={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            </div>
                            <div style={guideStyle('emailContent')}>
                            <RichTextField
                                minHeight={300}
                                mode={'html'}
                                label="Email Content"
                                value={emailContent}
                                onChange={(v) => setEmailContent(v)}
                                labelActions={ workingCart?.ordered || workingCart?.delivered ? undefined : <IconButton iconProps={{iconName: 'refresh'}} onClick={() => emailLookup.refetch()}/>}
                                readOnly={isDelivering || workingCart?.ordered || workingCart?.delivered}
                            />
                            </div>
                        </PivotItem>
                    </Pivot>
                    <Stack tokens={{ childrenGap: 10 }}>

                    </Stack>
                </React.Fragment>
            )}
        </Panel>
    );
};

/** One unsourceable line offered for the always-available override. */
interface IIncludeAnywayEntry {
    item: IWorkspaceItem;
    label: string;
    reason: string;
}

/**
 * The "Include anyway?" picker behind the sourcing-error message: choose which unsourceable lines get
 * the per-line always-available override (all are preselected; Select all covers the rest of the flow).
 */
const IncludeAnywayDialog: React.FC<{
    items: IIncludeAnywayEntry[];
    busy: boolean;
    onConfirm: (items: IWorkspaceItem[]) => void;
    onDismiss: () => void;
}> = ({items, busy, onConfirm, onDismiss}) => {
    const [selected, setSelected] = React.useState<Set<string>>(new Set(items.map((e) => e.item.id)));
    const allSelected = selected.size === items.length;
    const toggle = (id: string, checked?: boolean) => {
        setSelected((current) => {
            const next = new Set(current);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };
    return (
        <Dialog
            hidden={false}
            onDismiss={onDismiss}
            minWidth={440}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Include Anyway?',
                subText: 'These items have no vendor that can source them right now. Included items stay purchasable on this prepared cart regardless.'
            }}>
            <Stack tokens={{childrenGap: '0.5em'}}>
                {items.length > 1 &&
                    <Checkbox
                        label='Select all'
                        checked={allSelected}
                        indeterminate={selected.size > 0 && !allSelected}
                        onChange={(_, checked) => setSelected(checked ? new Set(items.map((e) => e.item.id)) : new Set())}
                    />}
                {items.map(({item, label, reason}) =>
                    <Checkbox
                        key={item.id}
                        checked={selected.has(item.id)}
                        onChange={(_, checked) => toggle(item.id, checked)}
                        onRenderLabel={() => (
                            <div style={{marginLeft: 4}}>
                                {label}
                                <div style={{fontSize: 12, color: '#666'}}>{reason}</div>
                            </div>
                        )}
                    />)}
            </Stack>
            <DialogFooter>
                <PrimaryButton
                    text={busy ? 'Including…' : `Include ${selected.size === items.length ? 'All' : `${selected.size}`}`}
                    disabled={busy || selected.size === 0}
                    onClick={() => onConfirm(items.filter((e) => selected.has(e.item.id)).map((e) => e.item))}
                />
                <DefaultButton text='Cancel' disabled={busy} onClick={onDismiss} />
            </DialogFooter>
        </Dialog>
    );
};

/** What the panel's deliver checks concluded, so the estimate can fold it into its one status bar. */
interface ICartReadiness {
    canDeliver: boolean;
    blocker?: string;
    blockerField?: BlockerField;
}

interface ICartIssue {
    key: string;
    /** Plain text, or a node when part of it acts (e.g. a link that opens the omnibox). */
    text: React.ReactNode;
    severity: 'error' | 'warning';
}

export const PreparedCartPriceEstimate: React.FC<{waitForSave?: boolean, workspaceId: string, storefrontId?: string, markupType?: string, markup?: number, minimumPrice?: number, onEstimate: (validEstimate: boolean, priceError: boolean | undefined, costError: boolean | undefined, minimumPriceError: boolean | undefined) => void, onEstimateBegin: () => void, onProblemItemsChange?: (itemIds: string[]) => void, readiness?: ICartReadiness}> = ({waitForSave, workspaceId, storefrontId, markupType, markup, minimumPrice, onEstimate, onEstimateBegin, onProblemItemsChange, readiness}) => {
    const [getEstimate] = useCalculatePriceEstimateMutation();
    const [putItemDetails] = usePutWorkspaceItemDetailsMutation();
    const workspaceProducts = useGetWorkspaceProductsQuery(workspaceId ?? '', {skip: !workspaceId || workspaceId === ''});
    // The grid stays editable beside the panel, so line changes (add/remove, quantity, include-anyway)
    // must re-run the estimate; this fingerprint is what the effect below watches.
    const productsKey = React.useMemo(() => (workspaceProducts.data ?? [])
        .map(i => `${i.id}:${i.details?.quantity ?? ''}:${i.details?.alwaysAvailable ? 1 : 0}`)
        .join('|'), [workspaceProducts.data]);
    const [estimate, setEstimate] = React.useState<number | undefined>(undefined);
    const [estimateCost, setEstimateCost] = React.useState<number | undefined>(undefined);

    const [isLoading, setIsLoading] = React.useState(false);
    const [messages, setMessages] = React.useState<{[key:string]: string}>({});
    const [showSourcingDetails, setShowSourcingDetails] = React.useState(false);
    const [issueIndex, setIssueIndex] = React.useState(0);
    const [overrideBusy, setOverrideBusy] = React.useState(false);
    const [overrideDialogOpen, setOverrideDialogOpen] = React.useState(false);
    // Bumped after applying always-available overrides so the estimate recalculates.
    const [overrideTick, setOverrideTick] = React.useState(0);

    // Only a workspace/storefront switch discards the previous estimate. Re-estimates for the same
    // scope (markup edits, saves, grid changes) keep the old numbers and messages on screen until the
    // new answer lands; resetting on every run made the sourcing highlights and the status bar flash.
    const estimateScope = React.useRef<string>();
    const estimateSequence = React.useRef(0);
    useEffect(() => {
        const scope = `${workspaceId}|${storefrontId ?? ''}`;
        if (estimateScope.current !== scope) {
            estimateScope.current = scope;
            estimateSequence.current += 1;
            setEstimate(undefined);
            setEstimateCost(undefined);
            setMessages({});
            setIsLoading(false);
        }
        const fetchEstimate = async () => {
            if(!storefrontId) return;
            const sequence = ++estimateSequence.current;
            setIsLoading(true);
            try {
                onEstimateBegin();
                await getEstimate({storefrontId, workspaceId, markupType, markup, minimumPrice}).unwrap().then((res) =>{
                        if (sequence !== estimateSequence.current) return;
                        if(res) {
                            setEstimate(res.price);
                            setEstimateCost(res.cost)
                            setMessages(res.messages ?? {});
                            
                            const validEstimate = !(res.messages != undefined && Object.keys(res.messages).length > 0)
                            const priceError = res.messages != undefined && Object.keys(res.messages).some(m => m.includes('Price'));
                            const costError = res.messages != undefined && Object.keys(res.messages).some(m => m.includes('Cost'));
                            const minimumPriceError = res.messages != undefined && Object.keys(res.messages).some(m => m.includes('MINIMUM_PRICE'));
                            onEstimate(validEstimate, priceError, costError, minimumPriceError);
                        }
                })
            } catch (error) {
                console.error("Failed to fetch estimate:", error);
            } finally {
                if (sequence === estimateSequence.current) setIsLoading(false);
            }
        };
        if(!waitForSave) {
            fetchEstimate().then();
        }
    }, [workspaceId, getEstimate, storefrontId, markupType, markup, minimumPrice, waitForSave, overrideTick, productsKey]);

    const findItemFromKey = (key: string): IWorkspaceItem | undefined =>
        workspaceProducts?.data?.find(a => a.productId === key)
        ?? workspaceProducts?.data?.find(a => a.id === key)
        ?? (key.endsWith('_BUNDLE')
            ? workspaceProducts?.data?.find(b => b.sourceEntityId === key.replace('_BUNDLE', '') && b.isBundle)
            : undefined);

    const itemLabel = (item: IWorkspaceItem) =>
        (item.isBundle ? item.adhoc?.name : item.product?.name) ?? item.adhoc?.name;

    // The workspace lines behind the sourcing messages, so the page can mark those rows.
    const problemItemIds = Object.keys(messages ?? {})
        .filter(key => !['Price', 'Cost', 'MINIMUM_PRICE'].includes(key))
        .map(key => findItemFromKey(key)?.id)
        .filter((id): id is string => !!id)
        .filter((id, index, all) => all.indexOf(id) === index);
    const problemItemsKey = problemItemIds.join('|');
    React.useEffect(() => {
        onProblemItemsChange?.(problemItemIds);
    }, [problemItemsKey]);

    const getItemNameFromKey = (key: string) => {
        const item = findItemFromKey(key);
        return item ? itemLabel(item) ?? key : key;
    }

    // Estimate errors that map to a workspace line can be forced in with the per-line
    // always-available override; lines already overridden are excluded.
    const overridableItems = Object.entries(messages ?? {})
        .filter(([key]) => !['Price', 'Cost', 'MINIMUM_PRICE'].includes(key))
        .map(([key, reason]) => {
            const item = findItemFromKey(key);
            return item && item.id && item.details?.alwaysAvailable !== true
                ? {item, label: itemLabel(item) ?? key, reason}
                : undefined;
        })
        .filter((entry): entry is {item: IWorkspaceItem, label: string, reason: string} => !!entry)
        .filter((entry, index, all) => all.findIndex(e => e.item.id === entry.item.id) === index);

    const includeAnyway = async (items: IWorkspaceItem[]) => {
        setOverrideBusy(true);
        try {
            await Promise.all(items.map(item => putItemDetails({
                workspace: item.workspace,
                product: item.id,
                details: {...item.details, alwaysAvailable: true}
            }).unwrap()));
            setOverrideDialogOpen(false);
            setOverrideTick(t => t + 1);
        } finally {
            setOverrideBusy(false);
        }
    };

    // ONE status bar for the cart. Everything that can be wrong is folded into a single list, ordered by
    // what to fix first, and an empty workspace short-circuits the rest (no products is the only real
    // problem then; the zero price and the missing estimate merely follow from it).
    const sourcingEntries = Object.entries(messages ?? {});
    const itemEntries = sourcingEntries.filter(([key]) => !['Price', 'Cost', 'MINIMUM_PRICE'].includes(key));
    const pricingEntries = sourcingEntries.filter(([key]) => ['Price', 'Cost', 'MINIMUM_PRICE'].includes(key));
    const workspaceIsEmpty = workspaceProducts.data !== undefined && workspaceProducts.data.length === 0;
    const warnMargin = estimate !== undefined && !!estimateCost && estimateCost >= estimate;

    const issues: ICartIssue[] = [];
    if (workspaceIsEmpty) {
        issues.push({
            key: 'empty', severity: 'error',
            text: <>
                This workspace has no products.{' '}
                <Link onClick={() => openGlobalSearch({mode: 'products'})} title='Search products and add them to this workspace without leaving the search'>
                    Add products
                </Link>
                {' '}before delivering the cart.
            </>
        });
    } else {
        if (itemEntries.length > 0) {
            issues.push({
                key: 'sourcing', severity: 'error',
                text: itemEntries.length === 1
                    ? `"${getItemNameFromKey(itemEntries[0][0])}" can't be sourced from a vendor right now.`
                    : `${itemEntries.length} items can't be sourced from a vendor right now.`
            });
        }
        for (const [key, message] of pricingEntries) issues.push({key: `pricing-${key}`, severity: 'error', text: message});
        if (warnMargin) issues.push({key: 'margin', severity: 'warning', text: 'Cost is greater than or equal to price, resulting in zero or negative margin.'});
        // The deliver checks' first blocker, unless it is just restating a pricing/sourcing issue above.
        if (readiness?.blocker && !readiness.canDeliver && readiness.blockerField !== 'pricing' && readiness.blockerField !== 'estimate') {
            issues.push({key: 'blocker', severity: 'error', text: readiness.blocker});
        }
    }

    const sourcingDetails = itemEntries.length === 0 ? null : (
        <div>
            {itemEntries.map(([key, message]) =>
                <div key={key} style={{fontSize: '90%'}}>{getItemNameFromKey(key)} - {message}</div>)}
        </div>
    );

    const getStatusBar = () => {
        if (issues.length === 0) {
            return readiness?.canDeliver
                ? <MessageBar messageBarType={MessageBarType.success} styles={{root: {marginTop: '0.5em'}}}>Ready To Deliver</MessageBar>
                : null;
        }
        const severe = issues.some(i => i.severity === 'error');
        // Pager: one issue at a time, "1 of N" with chevrons, so the bar stays a single line.
        const index = Math.min(issueIndex, issues.length - 1);
        const current = issues[index];
        const isSourcing = current.key === 'sourcing';
        const showDetails = isSourcing && showSourcingDetails;
        return (
            <>
                <MessageBar
                    messageBarType={severe ? MessageBarType.severeWarning : MessageBarType.warning}
                    messageBarIconProps={{iconName: 'Warning'}}
                    // Multiline so the message wraps in full instead of being clipped beside the pager.
                    isMultiline={true}
                    styles={{root: {marginTop: '0.5em'}, text: {lineHeight: '18px'}}}
                    actions={(isSourcing || issues.length > 1) ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.25em'}}>
                            {isSourcing && overridableItems.length > 0 &&
                                <MessageBarButton disabled={overrideBusy} onClick={() => setOverrideDialogOpen(true)}>
                                    Include anyway?
                                </MessageBarButton>}
                            {isSourcing &&
                                <MessageBarButton onClick={() => setShowSourcingDetails(v => !v)}>
                                    {showSourcingDetails ? 'Hide details' : 'Details'}
                                </MessageBarButton>}
                            {issues.length > 1 && <>
                                <IconButton iconProps={{iconName: 'ChevronLeft'}} ariaLabel='Previous issue' disabled={index === 0}
                                    onClick={() => setIssueIndex(index - 1)} styles={{root: {height: 24, width: 24}, icon: {fontSize: 10}}} />
                                <Text variant='small' styles={{root: {whiteSpace: 'nowrap'}}}>{index + 1} of {issues.length}</Text>
                                <IconButton iconProps={{iconName: 'ChevronRight'}} ariaLabel='Next issue' disabled={index === issues.length - 1}
                                    onClick={() => setIssueIndex(index + 1)} styles={{root: {height: 24, width: 24}, icon: {fontSize: 10}}} />
                            </>}
                        </div>
                    ) : undefined}>
                    {isSourcing && !showDetails
                        ? <TooltipHost content={sourcingDetails ?? undefined} calloutProps={{gapSpace: 0}}>
                            <span style={{cursor: 'help'}}>{current.text}</span>
                        </TooltipHost>
                        : current.text}
                    {showDetails && <div style={{marginTop: '0.5em'}}>{sourcingDetails}</div>}
                </MessageBar>
                {overrideDialogOpen &&
                    <IncludeAnywayDialog
                        items={overridableItems}
                        busy={overrideBusy}
                        onConfirm={(items) => void includeAnyway(items)}
                        onDismiss={() => setOverrideDialogOpen(false)}
                    />}
            </>
        );
    };

    const getCurrencyValue = (value?: number, currency?: string) => {
        const checkCurrency = (currency && currency.length > 0) ? currency : 'USD';
        try {
            return `${new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: checkCurrency
            }).format(value ?? 0)}`
        } catch (error) {
            console.error('Error formatting currency:', error);
            return `$${value ?? 0}`;
        }
    }

    // A spinner only before the first answer; later re-estimates refresh the numbers in place.
    if (isLoading && estimate === undefined) return <Spinner size={SpinnerSize.small} label="Calculating..." />;
    if (estimate === undefined) return <div>{!workspaceIsEmpty && 'Price estimate not available'}{getStatusBar()}</div>;

    const hasPositivePriceAndCost = estimate > 0 && (estimateCost ?? 0) > 0;

    return <div style={{paddingBottom: '1em'}}>
        {!workspaceIsEmpty &&
            // One row of figures, wrapping only when the panel is narrow.
            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: '1.5em', rowGap: '0.25em'}}>
                <div><b>Price:</b> {getCurrencyValue(estimate)}</div>
                <div><b>Cost:</b> {getCurrencyValue(estimateCost)}</div>
                {hasPositivePriceAndCost &&
                    <React.Fragment>
                        <div><b>Markup:</b> {(((estimate - (estimateCost ?? 0)) / (estimateCost ?? 1)) * 100).toFixed(2)} %</div>
                        <div><b>Margin:</b> {(((estimate - (estimateCost ?? 0)) / estimate) * 100).toFixed(2)} %</div>
                    </React.Fragment>
                }
            </div>}
        {getStatusBar()}
    </div>
}


class CompanySearchOption implements ISuggestionItem {
    constructor(
        private key: string,
        private text: string,
        private _value: string, // renamed to _value to avoid unused warning
    ) {}
    getSearchText: () => string = () => {
        return this.text;
    };
    getSuggestionItem() {
        return (
            <div key={this.key} className="suggestionItem">
                {this.text}
            </div>
        );
    }
    getKey: () => string = () => {
        return this.key;
    };
}